import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Settings, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { logGlobalError } from '../../utils/globalErrorLogger';
import { PortalMenu } from '../PortalMenu';
import { paths } from '../../routes/paths';

export function UserMenu() {
  const { currentUser, isAdmin, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const displayName = currentUser?.displayName || currentUser?.email || '';
  const photoURL = currentUser?.photoURL || null;
  const initial = getInitial(currentUser?.displayName, currentUser?.email);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label="Open account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm overflow-hidden ring-1 ring-primary/20 hover:ring-primary/40 focus:outline-none focus:ring-2 focus:ring-primary"
      >
        {photoURL ? (
          <img
            src={photoURL}
            alt=""
            className="h-full w-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span aria-hidden="true">{initial}</span>
        )}
      </button>

      {open && (
        <PortalMenu
          anchorRef={triggerRef}
          onRequestClose={() => setOpen(false)}
          role="menu"
          className="w-56 py-1"
        >
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
            {currentUser?.displayName && currentUser.email && (
              <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>
            )}
          </div>
          {isAdmin && (
            <MenuLink to={paths.admin()} icon={<Shield className="h-4 w-4" />} onSelect={() => setOpen(false)}>
              Admin
            </MenuLink>
          )}
            <MenuLink to={paths.settings()} icon={<Settings className="h-4 w-4" />} onSelect={() => setOpen(false)}>
            Settings
          </MenuLink>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              logout().catch((err) => {
                logGlobalError(err, { context: 'UserMenu.logout' });
              });
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </PortalMenu>
      )}
    </div>
  );
}

interface MenuLinkProps {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onSelect: () => void;
}

function MenuLink({ to, icon, children, onSelect }: MenuLinkProps) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onSelect}
      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
    >
      {icon}
      {children}
    </Link>
  );
}

function getInitial(displayName: string | null | undefined, email: string | null | undefined): string {
  const source = (displayName && displayName.trim()) || (email && email.trim()) || '';
  const first = source.charAt(0);
  return first ? first.toUpperCase() : '?';
}
